import { useMemo, useRef, useState } from 'react';
import API from '../../../utils/constant';
import { useQueries, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  useTeacherAssignmentCreate,
  useTeacherAssignmentDelete,
  useTeacherAssignments,
  useSectionsList,
} from '../../api/hooks';
import { Link } from 'react-router-dom';
import { Button, ConfirmDialog, IconButton, Initials, PageHeader, PagedFooter, PanelCard, PillTabs, Segmented, SortableTh, Table, TEXT_LINK, Td, Th, TH_CLASS } from '../../components/ui';
import { FaTrash } from '../../components/ui/icons';
import { Download } from 'lucide-react';
import { toCsv, downloadCsv } from '../../lib/csv';
import { useSortableTable, SortColumn } from '../../lib/useSortableTable';
import usePagination from '../../lib/usePagination';
import type { TeacherAssignment } from '../../api/types';
import { nameOr } from '../../lib/names';
import { teacherName, type TeacherProfile } from '../../lib/teachers';

const ASSIGNMENT_COLUMNS: SortColumn<TeacherAssignment>[] = [
  { key: 'teacher', accessor: (a) => nameOr(a.teacher_name, a.teacher_username) },
  { key: 'grade', accessor: (a) => a.school_grade_name },
  { key: 'subject', accessor: (a) => a.subject_name },
  { key: 'section', accessor: (a) => (a.section ? a.section.name : '') },
  { key: 'homeroom', accessor: (a) => (a.is_homeroom ? 'yes' : 'no') },
];

// Actual backend shapes:
//   GET /api/school/teachers/  → { teachers: [{ profile_id, user_id, username, first_name, last_name }] }
//   GET /api/school/grades/    → { grades: [{ id, grade_name, name? }] }
//   GET /api/school/grades/<id>/subjects/ → { subjects: [{ id, subject_name }] }   (the subjects this school offers in that grade)
interface GradeItem   { id: number; grade_name?: string; name?: string; }
interface SubjectItem { id: number; name: string; }

const useTeachers = () =>
  useQuery({
    queryKey: ['school', 'teacher_profiles'],
    queryFn: () =>
      API.get<{ teachers: TeacherProfile[] }>('/api/school/teachers/').then(
        (r) => r.data.teachers ?? [],
      ),
  });

const useGrades = () =>
  useQuery({
    queryKey: ['school', 'grades'],
    queryFn: () =>
      API.get<{ grades: GradeItem[] }>('/api/school/grades/').then(
        (r) => r.data.grades ?? [],
      ),
  });

// One request per grade: only the subjects the school actually offers in that grade. (The global
// /api/school/subjects/ list returns every subject in the database, so it cannot size the matrix.)
const useGradeSubjects = (gradeIds: number[]) =>
  useQueries({
    queries: gradeIds.map((id) => ({
      queryKey: ['subjects', String(id)], // same cache entry as ClassSetup's useGradeSubjects
      queryFn: () =>
        API.get<{ subjects: { id: number; subject_name?: string; name?: string }[] }>(
          `/api/school/grades/${id}/subjects/`,
        ).then((r) => r.data),
      select: (d: { subjects?: { id: number; subject_name?: string; name?: string }[] }): SubjectItem[] => (d.subjects ?? []).map((x) => ({ id: x.id, name: x.subject_name ?? x.name ?? `Subject ${x.id}` })),
    })),
  });

type View = 'matrix' | 'list';

const fieldCls =
  'min-h-[44px] w-full rounded-[12px] border border-line bg-surface px-3.5 text-sm text-ink outline-none transition focus:border-navy focus:ring-2 focus:ring-accent/40 dark:focus:border-mist-fixed';

// "Ato Getachew M." -> "Getachew M."
const shortName = (name: string) => name.replace(/^(Ato|W\/ro|Ato\.|W\/rt)\s+/i, '');
const gradeName = (g: GradeItem) => g.grade_name || g.name || `Grade ${g.id}`;
const asName = (a: TeacherAssignment) => nameOr(a.teacher_name, a.teacher_username);
const LOAD_SEGMENTS = 6;

// Design `.cell`: 96x34 pill; the other states below override its fill.
const CELL = 'inline-flex min-h-[34px] min-w-[96px] items-center justify-center rounded-[10px] px-2.5 text-xs font-semibold';

export default function TeachersAssignmentsPage() {
  const list     = useTeacherAssignments();
  const create   = useTeacherAssignmentCreate();
  const del      = useTeacherAssignmentDelete();
  const teachers = useTeachers();
  const grades   = useGrades();

  const [view, setView] = useState<View>('matrix');
  const [focus, setFocus] = useState<number | null>(null);
  const [pendingDelete, setPendingDelete] = useState<TeacherAssignment | null>(null);
  const teacherRef = useRef<HTMLSelectElement>(null);

  const [teacher, setTeacher] = useState<number | ''>('');
  const [grade,   setGrade]   = useState<number | ''>('');
  const [subject, setSubject] = useState<number | '' | 'homeroom'>('');
  // '' = the whole grade (subject assignments only); a homeroom teacher always has one section.
  const [section, setSection] = useState<number | ''>('');
  const sections = useSectionsList(grade === '' ? null : grade);
  const sectionList = sections.data?.sections ?? [];

  const { rows: sortedAssignments, sort, toggle } = useSortableTable(
    list.data, ASSIGNMENT_COLUMNS, { key: 'teacher', dir: 'asc' },
  );

  const assignments = list.data ?? [];
  const teacherList = teachers.data ?? [];
  const gradeList = grades.data ?? [];
  const gradeSubjects = useGradeSubjects(gradeList.map((g) => g.id));
  const subjectsLoading = grades.isLoading || gradeSubjects.some((q) => q.isLoading);
  const subjectsError = grades.isError || gradeSubjects.some((q) => q.isError);
  const subjectsReady = !!grades.data && gradeSubjects.every((q) => q.isSuccess);
  // offered[gradeId] = the subjects that grade has; undefined while unknown (loading or failed).
  const offered = new Map<number, SubjectItem[]>();
  gradeList.forEach((g, i) => { const d = gradeSubjects[i]?.data; if (d) offered.set(g.id, d); });
  const isOffered = (gradeId: number, subjectId: number) => offered.get(gradeId)?.some((x) => x.id === subjectId) ?? true;
  // Matrix rows: every subject offered in at least one grade, in grade order.
  const subjectList = (() => {
    const m = new Map<number, SubjectItem>();
    for (const g of gradeList) for (const x of offered.get(g.id) ?? []) if (!m.has(x.id)) m.set(x.id, x);
    return Array.from(m.values());
  })();

  // Per-teacher load, derived from the assignments already loaded.
  const loads = useMemo(() => {
    const m = new Map<number, { n: number; subjects: string[] }>();
    for (const a of list.data ?? []) {
      const e = m.get(a.teacher) ?? { n: 0, subjects: [] };
      e.n += 1;
      if (a.subject_name && !e.subjects.includes(a.subject_name)) e.subjects.push(a.subject_name);
      m.set(a.teacher, e);
    }
    return m;
  }, [list.data]);
  const teacherPg = usePagination(teacherList, 10);
  const matrixRows = [{ id: null as number | null, name: 'Homeroom' }, ...subjectList.map((s) => ({ id: s.id as number | null, name: s.name }))];
  const matrixPg = usePagination(matrixRows, 10);
  const assignmentPg = usePagination(sortedAssignments, 10, `${sort?.key}|${sort?.dir}`);
  // Offered subject x grade cells with nobody teaching them (homeroom row excluded; "Not offered" cells never count).
  const openSlots = subjectList.reduce(
    (n, s) => n + gradeList.filter((g) => isOffered(g.id, s.id) && !assignments.some((a) => a.school_grade === g.id && a.subject === s.id)).length,
    0,
  );
  const formSubjects = grade === '' ? [] : offered.get(grade) ?? [];
  const focusName = focus != null ? teacherList.find((t) => t.profile_id === focus) : undefined;

  function cellAssignments(gradeId: number, subjectId: number | null) {
    return assignments.filter((a) => a.school_grade === gradeId && (subjectId == null ? a.is_homeroom : a.subject === subjectId));
  }

  function prefill(gradeId: number, subjectId: number | 'homeroom') {
    setGrade(gradeId);
    setSubject(subjectId);
    setSection('');
    teacherRef.current?.focus();
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!teacher || !grade) return;
    const isHomeroom = subject === 'homeroom';
    if (isHomeroom && section === '') return;
    create.mutate(
      {
        teacher: Number(teacher),
        school_grade: Number(grade),
        subject: isHomeroom || subject === '' ? null : Number(subject),
        section: section === '' ? null : Number(section),
        is_homeroom: isHomeroom,
      },
      {
        onSuccess: () => {
          toast.success('Assigned');
          setTeacher('');
          setGrade('');
          setSubject('');
          setSection('');
        },
        onError: (e: any) => {
          const d = e?.response?.data;
          const first = d && typeof d === 'object' ? Object.values(d).flat().find((v) => typeof v === 'string') : null;
          toast.error((first as string | null) ?? 'Failed to assign');
        },
      },
    );
  }

  const labelCls = 'mb-1.5 block font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted';

  const deleteLabel = (a: TeacherAssignment) =>
    `Remove ${asName(a)} from ${a.school_grade_name}${a.section ? ` section ${a.section.name}` : ''} ${a.is_homeroom ? 'all subjects' : a.subject_name ?? 'all subjects'}`;

  function exportCsv() {
    downloadCsv('teacher-assignments.csv', toCsv(
      ['Teacher', 'Grade', 'Section', 'Subject', 'Homeroom'],
      sortedAssignments.map((a) => [asName(a), a.school_grade_name ?? '', a.section ? a.section.name : 'Whole grade', a.is_homeroom ? 'All subjects' : a.subject_name ?? 'All subjects', a.is_homeroom ? 'Yes' : 'No']),
    ));
  }

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        eyebrow="Setup · Step 3 of 3"
        title="Teachers & assignments"
        subtitle={
          <>
            {teacherList.length} teachers · {assignments.length} assignments
            {list.data && subjectsReady && (
              <> · <span className="font-semibold text-accent-ink">{openSlots} subject {openSlots === 1 ? 'slot' : 'slots'} without a teacher</span></>
            )}
          </>
        }
        actions={
          <>
            <Segmented
              label="View"
              value={view}
              onChange={setView}
              options={[{ key: 'matrix', label: 'Matrix' }, { key: 'list', label: 'List' }]}
            />
            <Button variant="ghost" icon={<Download size={18} strokeWidth={1.8} aria-hidden />} onClick={exportCsv} disabled={!list.data || list.data.length === 0}>
              Export
            </Button>
          </>
        }
      />

      <PanelCard title="Assign a teacher" subtitle={'Pick a subject for the whole grade or one section. "Homeroom" gives a teacher every subject in one section.'} className="mb-[18px]">
        <form onSubmit={submit} className="grid gap-3.5">
          <div className="md:max-w-[360px]">
            <label htmlFor="ta-teacher" className={labelCls}>Teacher</label>
            <select
              id="ta-teacher"
              ref={teacherRef}
              value={teacher}
              onChange={(e) => setTeacher(Number(e.target.value) || '')}
              className={fieldCls}
            >
              <option value="">Select teacher…</option>
              {teachers.isLoading && <option disabled>Loading…</option>}
              {teachers.isError && <option disabled>Teacher list unavailable</option>}
              {teacherList.map((t) => (
                <option key={t.profile_id} value={t.profile_id}>{teacherName(t)}</option>
              ))}
            </select>
          </div>
          <div className="min-w-0">
            <span className={labelCls} aria-hidden="true">Grade</span>
            {grades.isLoading ? (
              <p className="text-sm text-muted">Loading grades…</p>
            ) : grades.isError ? (
              <p className="text-sm text-danger">Grade list unavailable</p>
            ) : gradeList.length === 0 ? (
              <p className="text-sm text-muted">No grades yet.</p>
            ) : (
              <PillTabs
                scroll
                dense
                label="Grade"
                value={grade === '' ? '' : String(grade)}
                onChange={(k) => {
                  const next = Number(k) || '';
                  setGrade(next);
                  setSection('');
                  // A subject the new grade does not offer cannot stay selected.
                  if (typeof subject === 'number' && next !== '' && !(offered.get(next) ?? []).some((x) => x.id === subject)) setSubject('');
                }}
                options={gradeList.map((g) => ({ key: String(g.id), label: gradeName(g) }))}
              />
            )}
          </div>
          {grade !== '' && (
            <div className="min-w-0">
              <span className={labelCls} aria-hidden="true">Subject</span>
              {subjectsLoading ? (
                <p className="text-sm text-muted">Loading subjects…</p>
              ) : (
                <>
                  <PillTabs
                    scroll
                    dense
                    label="Subject"
                    value={subject === '' ? '' : String(subject)}
                    onChange={(k) => setSubject(k === 'homeroom' ? 'homeroom' : Number(k) || '')}
                    options={[
                      { key: 'homeroom', label: 'Homeroom (all subjects)' },
                      ...formSubjects.map((s) => ({ key: String(s.id), label: s.name })),
                    ]}
                  />
                  {subjectsError && <p className="mt-1.5 text-sm text-danger">Subject list unavailable</p>}
                </>
              )}
            </div>
          )}
          {grade !== '' && subject !== '' && (
            <div className="min-w-0">
              <span className={labelCls} aria-hidden="true">Section</span>
              {sections.isLoading ? (
                <p className="text-sm text-muted">Loading sections…</p>
              ) : sections.isError ? (
                <p className="text-sm text-danger">Section list unavailable</p>
              ) : (
                <>
                  {sectionList.length > 0 && (
                    <PillTabs
                      scroll
                      dense
                      label="Section"
                      value={section === '' ? (subject === 'homeroom' ? '' : 'whole') : String(section)}
                      onChange={(k) => setSection(k === 'whole' ? '' : Number(k) || '')}
                      options={[
                        ...(subject === 'homeroom' ? [] : [{ key: 'whole', label: 'Whole grade' }]),
                        ...sectionList.map((sec) => ({ key: String(sec.id), label: `Section ${sec.name}` })),
                      ]}
                    />
                  )}
                  {sectionList.length === 0 ? (
                    <p className="text-sm text-muted">
                      This grade has no sections yet{subject === 'homeroom' ? ', and a homeroom teacher needs one' : ', so the teacher gets the whole grade'}.{' '}
                      <Link to="/settings/sections" className="font-semibold text-primary underline">Create sections</Link>
                    </p>
                  ) : subject === 'homeroom' && section === '' ? (
                    <p className="mt-1.5 text-sm text-muted">Pick the section this teacher is homeroom for.</p>
                  ) : null}
                </>
              )}
            </div>
          )}
          <div>
            <Button type="submit" variant="secondary" disabled={create.isPending || !teacher || !grade || (subject === 'homeroom' && section === '')}>
              {create.isPending ? 'Assigning…' : 'Assign'}
            </Button>
          </div>
        </form>
      </PanelCard>

      {view === 'matrix' ? (
        <div className="grid gap-[18px] min-[821px]:grid-cols-2 min-[1181px]:grid-cols-[minmax(0,4fr)_minmax(0,8fr)]">
          <PanelCard
            title="Teachers"
            subtitle="Select one to highlight their load"
            actions={focus != null ? (
              <button type="button" onClick={() => setFocus(null)} className={TEXT_LINK}>Clear</button>
            ) : undefined}
          >
            {teachers.isLoading ? (
              <p className="py-6 text-center text-sm text-muted">Loading teachers…</p>
            ) : teachers.isError ? (
              <p className="py-6 text-center text-sm text-danger">Teacher list unavailable</p>
            ) : teacherList.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted">No teachers yet.</p>
            ) : (
              <div className="flex flex-col gap-1.5">
                {teacherPg.pageItems.map((t) => {
                  const name = teacherName(t);
                  const load = loads.get(t.profile_id) ?? { n: 0, subjects: [] };
                  const on = focus === t.profile_id;
                  const filled = Math.min(load.n, LOAD_SEGMENTS);
                  return (
                    <button
                      key={t.profile_id}
                      type="button"
                      aria-pressed={on}
                      onClick={() => setFocus(on ? null : t.profile_id)}
                      className={`flex min-h-[56px] w-full items-center gap-3 rounded-[14px] border p-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${on ? 'border-navy bg-navy text-white' : 'border-transparent hover:bg-panel'}`}
                    >
                      <Initials name={name} className={on ? '!bg-accent !text-navy' : ''} />
                      <span className="min-w-0 flex-1">
                        <span className={`block truncate font-semibold ${on ? 'text-white' : 'text-ink'}`}>{name}</span>
                        <span className={`block truncate text-xs ${on ? 'text-on-navy-muted' : 'text-muted'}`}>{load.subjects.join(', ') || 'No subjects yet'}</span>
                      </span>
                      <span className="flex flex-col items-end gap-1.5">
                        <span className={`font-mono text-[11px] font-semibold ${on ? 'text-on-navy-muted' : 'text-muted'}`}>{load.n} classes</span>
                        <span aria-hidden className="flex w-[60px] gap-0.5">
                          {Array.from({ length: LOAD_SEGMENTS }, (_, k) => (
                            <span key={k} className={`h-1.5 flex-1 rounded-[2px] ${k < filled ? (on ? 'bg-accent' : 'bg-slate') : (on ? 'bg-navy-3' : 'bg-line')}`} />
                          ))}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            {teacherList.length > 0 && <PagedFooter from={teacherPg.from} to={teacherPg.to} total={teacherPg.total} page={teacherPg.page} pageCount={teacherPg.pageCount} onPage={teacherPg.setPage} className="mt-3" />}
          </PanelCard>

          <PanelCard
            title="Who teaches what"
            subtitle={focusName
              ? `${teacherName(focusName)} · ${loads.get(focusName.profile_id)?.n ?? 0} classes highlighted`
              : 'Subjects × grades'}
            actions={
              <p className="flex flex-wrap gap-3.5 text-xs text-muted">
                <span className="flex items-center gap-1.5"><span aria-hidden className="inline-block h-2.5 w-2.5 rounded-[3px] border border-accent-line bg-accent-bg" />Homeroom</span>
                <span className="flex items-center gap-1.5"><span aria-hidden className="inline-block h-2.5 w-2.5 rounded-[3px] border-[1.5px] border-dashed border-accent" />Needs a teacher</span>
              </p>
            }
          >
            {list.isLoading ? (
              <p className="py-6 text-center text-sm text-muted">Loading assignments…</p>
            ) : list.isError ? (
              <p className="py-6 text-center text-sm text-danger">Could not load assignments.</p>
            ) : (
              <>
              {gradeList.map((g, i) => gradeSubjects[i]?.isError && (
                <p key={g.id} role="alert" className="mb-3 flex flex-wrap items-center gap-2 rounded-[12px] bg-danger-bg px-3 py-2 text-[13px] text-danger-ink">
                  Subjects for {gradeName(g)} could not be loaded, so its column may be incomplete.
                  <button type="button" onClick={() => gradeSubjects[i].refetch()} className={TEXT_LINK}>Retry</button>
                </p>
              ))}
              <Table minWidth={560}>
                <thead>
                  <tr>
                    <Th>Subject</Th>
                    {gradeList.map((g) => (
                      <Th key={g.id} align="center">{gradeName(g)}</Th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {matrixPg.pageItems.map((row) => (
                    <tr key={row.id ?? 'homeroom'}>
                      <Td className="font-semibold text-ink">{row.name}</Td>
                      {gradeList.map((g) => {
                        const cell = cellAssignments(g.id, row.id);
                        if (cell.length === 0) {
                          if (row.id != null && !isOffered(g.id, row.id)) {
                            return (
                              <Td key={g.id} align="center">
                                <span className={`${CELL} font-medium text-control`}>Not offered</span>
                              </Td>
                            );
                          }
                          return (
                            <Td key={g.id} align="center">
                              <button
                                type="button"
                                aria-label={`Assign a teacher to ${row.name} in ${gradeName(g)}`}
                                onClick={() => prefill(g.id, row.id ?? 'homeroom')}
                                className={`${CELL} whitespace-nowrap border-[1.5px] border-dashed border-accent bg-transparent text-accent-ink hover:bg-accent-bg`}
                              >
                                + Assign
                              </button>
                            </Td>
                          );
                        }
                        // One teacher can hold several section-level assignments in a cell: show each once, with their sections.
                        const byTeacher = new Map<number, TeacherAssignment[]>();
                        for (const a of cell) byTeacher.set(a.teacher, [...(byTeacher.get(a.teacher) ?? []), a]);
                        const uniq = Array.from(byTeacher.values()).map((list) => list[0]);
                        const withSections = (a: TeacherAssignment, name: string) => {
                          const own = byTeacher.get(a.teacher) ?? [];
                          if (own.some((x) => !x.section)) return name;  // whole grade
                          return `${name} (${own.map((x) => x.section!.name).sort().join(', ')})`;
                        };
                        const hi = focus != null && cell.some((a) => a.teacher === focus);
                        const dim = focus != null && !hi;
                        const look = row.id == null
                          ? (hi ? 'bg-accent text-navy' : 'bg-accent-bg text-accent-ink')
                          : (hi ? 'bg-navy text-white' : 'bg-ground text-ink');
                        return (
                          <Td key={g.id} align="center">
                            <span title={uniq.map((a) => withSections(a, asName(a))).join(', ')} className={`${CELL} ${look} ${dim ? 'opacity-[.35]' : ''}`}>
                              {uniq.map((a) => withSections(a, shortName(asName(a)))).join(', ')}
                            </span>
                          </Td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </Table>
              </>
            )}
            {!list.isLoading && !list.isError && <PagedFooter from={matrixPg.from} to={matrixPg.to} total={matrixPg.total} page={matrixPg.page} pageCount={matrixPg.pageCount} onPage={matrixPg.setPage} className="mt-3" />}
          </PanelCard>
        </div>
      ) : (
        <PanelCard title="Current assignments" subtitle="Sorted by teacher, A→Z">
          {list.isLoading ? (
            <div className="p-8 text-center text-sm font-medium text-muted">Loading assignments…</div>
          ) : list.isError ? (
            <div className="p-8 text-center text-sm font-medium text-danger">Could not load assignments.</div>
          ) : (
            <Table minWidth={620}>
              <thead>
                <tr>
                  <SortableTh columnKey="teacher" sort={sort} onToggle={toggle} className="border-b border-line">Teacher</SortableTh>
                  <SortableTh columnKey="grade" sort={sort} onToggle={toggle} className="border-b border-line">Grade</SortableTh>
                  <SortableTh columnKey="subject" sort={sort} onToggle={toggle} className="border-b border-line">Subject</SortableTh>
                  <SortableTh columnKey="section" sort={sort} onToggle={toggle} className="border-b border-line">Section</SortableTh>
                  <SortableTh columnKey="homeroom" sort={sort} onToggle={toggle} className="border-b border-line">Homeroom</SortableTh>
                  <th scope="col" className={TH_CLASS}><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {assignmentPg.pageItems.map((a) => (
                  <tr key={a.id}>
                    <Td>
                      <span className="flex items-center gap-2.5">
                        <Initials name={asName(a)} className="!h-[30px] !w-[30px] !rounded-[10px] !text-[11px]" />
                        <span className="font-semibold text-ink">{asName(a)}</span>
                      </span>
                    </Td>
                    <Td className="text-ink">{a.school_grade_name}</Td>
                    <Td className="text-ink">{a.is_homeroom ? 'All subjects' : a.subject_name ?? 'All subjects'}</Td>
                    <Td className={a.section ? 'text-ink' : 'text-muted'}>{a.section ? `Section ${a.section.name}` : 'Whole grade'}</Td>
                    <Td>
                      {a.is_homeroom && <span className="inline-flex items-center whitespace-nowrap rounded-full bg-accent-bg px-2.5 py-1 text-xs font-semibold text-accent-ink">Homeroom</span>}
                    </Td>
                    <Td align="right">
                      <IconButton
                        tone="danger"
                        label={deleteLabel(a)}
                        onClick={() => setPendingDelete(a)}
                        icon={<FaTrash size={18} strokeWidth={1.8} aria-hidden />}
                        className="border-transparent bg-transparent"
                      />
                    </Td>
                  </tr>
                ))}
                {sortedAssignments.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-sm font-medium text-muted">
                      No assignments yet. Use the form above to assign a teacher.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
          {!list.isLoading && !list.isError && <PagedFooter from={assignmentPg.from} to={assignmentPg.to} total={assignmentPg.total} page={assignmentPg.page} pageCount={assignmentPg.pageCount} onPage={assignmentPg.setPage} className="mt-3" />}
        </PanelCard>
      )}

      <ConfirmDialog
        isOpen={pendingDelete != null}
        title="Remove assignment?"
        body={pendingDelete ? `${asName(pendingDelete)} will no longer teach ${pendingDelete.is_homeroom ? 'homeroom' : pendingDelete.subject_name ?? 'all subjects'} in ${pendingDelete.school_grade_name}.` : undefined}
        confirmLabel="Remove"
        busy={del.isPending}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete) del.mutate(pendingDelete.id);
          setPendingDelete(null);
        }}
      />
    </div>
  );
}
